/**
 * Make a failed callable invocation say WHICH callable failed.
 *
 * THE DEFECT THIS CLOSES (OFFICEHUB-CLIENT-69). An event on
 * `/admin/issue-triage` read `FirebaseError: internal` — no stack, no callable
 * name, nothing to act on. The mechanism is general, not specific to that page:
 * a `FirebaseError` from the callable SDK carries a one-word `code` as its
 * message and no application stack, so whichever consumer reports it
 * (`consoleAndNetworkCapture` when a handler `console.error`s it, an error
 * boundary, an unhandled rejection) produces an event titled by the code alone.
 * Every callable in the app that fails this way groups into the same
 * undiagnosable bucket.
 *
 * WHY RENAME THE ERROR INSTEAD OF ADDING A REPORT. A second `captureMessage`
 * would double-count against the network interceptor, which already reports the
 * HTTP 5xx for the same failure — and that trade is exactly why
 * `reportCallableRejection` excludes the 5xx-class codes. Naming the error at
 * the throw fixes every downstream consumer at once, and adds no events.
 *
 * WHAT IT DELIBERATELY DOES NOT CARRY. The server's message text, and it does
 * not attach the original rejection as `.cause` — Sentry's default
 * LinkedErrors integration walks `cause` and would ship the original as a
 * chained exception inside the same event (see the constructor). Callable
 * messages routinely quote customer names, addresses and free-text notes
 * (`Schedule block not found: <id>`, form validation echoes), and the message
 * becomes the Sentry TITLE. The original error is preserved on `originalError`
 * for a debugger, and its text on `serverMessage` for the UI; only name + code
 * reach the title.
 *
 * Imports nothing — it is shared by all four PWAs through the `@shared` alias
 * (see buildIdentity.ts for why a bare specifier here is a build hazard).
 */

/**
 * The `code` property, contributed by DECLARATION MERGING rather than by a
 * class field.
 *
 * 🔴 THIS IS LOAD-BEARING, not a style choice. A class field declaration
 * (`readonly code?: string`) is EMITTED as a real own property initialised to
 * `undefined` — babel-preset-react-app compiles class fields with
 * `Object.defineProperty`, so the property exists on every instance whether or
 * not it was ever assigned. Every wrapped error, including one wrapping a
 * plain `Error` or an offline `TypeError`, would then answer `'code' in err`
 * with `true` while `err.code` is `undefined`.
 *
 * That is not cosmetic. The app's React Query retry predicate branched on
 * exactly `'code' in error` and then called `code.startsWith('auth/')`, so the
 * property's mere EXISTENCE threw a TypeError inside the predicate for every
 * codeless rejection — on the offline path, i.e. when the app is already
 * degraded. See `lib/queryRetryPolicy.ts`.
 *
 * A merged interface emits nothing, and the constructor assigns only when
 * there is a string to assign, so `'code' in err` is false when there is no
 * code. (`declare readonly code?: string` would say the same thing to
 * TypeScript but this toolchain rejects it: CRA's Babel pipeline runs
 * class-properties before preset-typescript and errors on `declare` fields.)
 *
 * Not marked `readonly`: the constructor must be able to assign it, and a
 * merged `readonly` member is not assignable even there. Treat it as readonly.
 */
export interface CallableInvocationError {
  /** The original `functions/...` code, when the rejection carried one. */
  code?: string;
}

/** An error that names the callable it came from. */
export class CallableInvocationError extends Error {
  /** The callable's exported function name, e.g. `updateScheduleBlock`. */
  readonly callableName: string;
  /**
   * The server's own message, kept OFF the title and available to the UI.
   *
   * The toast for an actionable rejection ("the Service App does not accept
   * block type X yet") is the server's text, and renaming the error must not
   * silently downgrade that to a generic "Failed to ...". So the text stays
   * reachable HERE while the Sentry-visible `message` carries only name + code
   * -- see actionableMessage() in useScheduleBlockMutations.ts.
   */
  readonly serverMessage?: string;
  /**
   * The rejection this wraps. NAMED `originalError`, never `cause` — see the
   * constructor.
   */
  readonly originalError: unknown;

  constructor(callableName: string, code: string | undefined, cause: unknown) {
    super(`${callableName} failed: ${code ? code.replace(/^functions\//, '') : 'unknown'}`);
    this.name = 'CallableInvocationError';
    this.callableName = callableName;
    // Assigned ONLY when there is a string — see the `code` declaration above.
    if (typeof code === 'string') this.code = code;
    const original = (cause as { message?: unknown } | null)?.message;
    this.serverMessage = typeof original === 'string' ? original : undefined;
    // 🔴 DELIBERATELY NOT `cause` (found in security review, fixed here).
    // Sentry's LinkedErrors integration is ON BY DEFAULT -- verified in the
    // installed SDK: `getDefaultIntegrations()` in @sentry/browser includes
    // `linkedErrorsIntegration()`, whose `DEFAULT_KEY` is the string "cause"
    // (@sentry/core/build/cjs/integrations/linkederrors.js) -- and every init
    // in this repo passes `integrations` as an ARRAY, which MERGES with the
    // defaults rather than replacing them. So an error attached as `.cause`
    // is walked and shipped as a CHAINED EXCEPTION in the same event: the
    // server's text would have arrived in the event body, which is exactly
    // what naming the error is supposed to prevent. It is not in the title,
    // and it would not have been a regression (the bare FirebaseError already
    // carried it) -- but it would have made this fix cosmetic.
    this.originalError = cause;
  }
}

function codeOf(err: unknown): string | undefined {
  try {
    const code = (err as { code?: unknown } | null)?.code;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Wrap a callable rejection in an error that names the callable.
 *
 * `code` is copied onto the new error, so anything that classifies by code —
 * `reportCallableRejection`, a retry predicate, a UI branch — is unaffected.
 */
export function nameCallableFailure(callableName: string, err: unknown): CallableInvocationError {
  return new CallableInvocationError(callableName, codeOf(err), err);
}

/**
 * The message a UI should show for a rejection: the server's own text when the
 * error has been renamed, otherwise the error's message. Use this anywhere a
 * handler reads `err.message` for display.
 */
export function callableFailureMessage(err: unknown): string {
  const named = (err as { serverMessage?: unknown } | null)?.serverMessage;
  if (typeof named === 'string') return named;
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : '';
}

/**
 * Invoke a callable and, on rejection, throw a NAMED error instead of the bare
 * `FirebaseError`. A success is returned untouched.
 */
export async function invokeNamedCallable<T>(
  callableName: string,
  invoke: () => Promise<T>,
): Promise<T> {
  try {
    return await invoke();
  } catch (err) {
    throw nameCallableFailure(callableName, err);
  }
}
